import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError, fmtDuration, getPlaybackUrl, getDistributionUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Modal, ModalField, ModalTextarea, ModalSelect } from "@/pages/Channels";
import { BrandPreview } from "@/components/BrandPreview";
import { ChannelPreviewPlayer } from "@/components/ChannelPreviewPlayer";
import { BrandControls } from "@/components/ChannelWizard";
import { ContentPicker } from "@/components/ContentPicker";
import { Play, Stop, PencilSimple, Trash, Broadcast, ArrowLeft, CalendarBlank, Copy, Repeat, ArrowsClockwise, ImageSquare, ListPlus, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChannelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "operator"].includes(user?.role);
  const canDelete = ["owner", "admin"].includes(user?.role);

  const [channel, setChannel] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [output, setOutput] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [viewers, setViewers] = useState(0);
  const [readiness, setReadiness] = useState(null);
  const [playbackUrl, setPlaybackUrl] = useState(null);
  const [showContent, setShowContent] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [draftMeta, setDraftMeta] = useState(null);
  const [draftPlaylist, setDraftPlaylist] = useState([]);
  const [draftBrand, setDraftBrand] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [ch, sc, outs, rd] = await Promise.all([
      api.get(`/channels/${id}`), api.get(`/channels/${id}/schedule`), api.get("/outputs"),
      api.get(`/channels/${id}/readiness`),
    ]);
    setChannel(ch.data);
    setSchedule(sc.data);
    setOutput(outs.data.find((o) => o.channel_id === id) || null);
    setReadiness(rd.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchNP = () => api.get(`/channels/${id}/now-playing`).then((r) => setNowPlaying(r.data)).catch(() => {});
    fetchNP();
    const t = setInterval(fetchNP, 5000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    const fetchViewers = () => api.get("/channels/viewers")
      .then((r) => setViewers((r.data.counts || {})[id] || 0)).catch(() => {});
    fetchViewers();
    const t = setInterval(fetchViewers, 10000);
    return () => clearInterval(t);
  }, [id]);

  // Canonical playback: resolve a PAS session playbackUrl (only for live channels — playback is
  // authorized once published). The preview player + manifest links consume this, never a raw
  // manifest URL.
  useEffect(() => {
    if (channel?.status === "live") {
      getPlaybackUrl(id).then(setPlaybackUrl).catch(() => setPlaybackUrl(null));
    } else {
      setPlaybackUrl(null);
    }
  }, [id, channel?.status]);

  const setStatus = async (status) => {
    try {
      await api.post(`/channels/${id}/status`, { status });
      toast.success(`Channel set ${status.replace("_", " ")}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const goLive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/channels/${id}/publish`);
      if (data.published) {
        toast.success("Channel published — now live");
      } else {
        const missing = data.readiness.checks.filter((c) => !c.ok).map((c) => c.label).join(", ");
        toast.error(`Not ready to publish: ${missing}`);
      }
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const createOutput = async () => {
    try { await api.post(`/channels/${id}/outputs`); toast.success("HLS output created"); load(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const moveToTrash = async () => {
    if (channel?.status === "live" || channel?.status === "preparing") {
      toast.error("Take the channel off air before moving it to Trash.");
      return;
    }
    if (!window.confirm(
      "Move this channel to Trash?\n\n• It will leave the active Channels list\n• Its media assets will NOT be deleted\n• You can restore it later from Trash"
    )) return;
    try {
      await api.post(`/channels/${id}/trash`);
      toast.success("Channel moved to Trash");
      navigate("/channels");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const openContent = () => { setDraftPlaylist(channel.loop_playlist || []); setShowContent(true); };
  const savePlaylist = async () => {
    setBusy(true);
    try {
      await api.put(`/channels/${id}/playlist`, { loop_playlist: draftPlaylist });
      toast.success("Content updated");
      setShowContent(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openBrand = () => { setDraftBrand(channel.brand || {}); setShowBrand(true); };
  const saveBrand = async () => {
    setBusy(true);
    try {
      await api.put(`/channels/${id}/brand`, draftBrand);
      toast.success("Branding updated");
      setShowBrand(false);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const convertMode = async (mode) => {
    if (!window.confirm(`Convert this channel to ${mode === "loop" ? "Continuous Loop" : "Scheduled"} mode?`)) return;
    setBusy(true);
    try {
      await api.post(`/channels/${id}/convert`, { mode });
      toast.success(`Converted to ${mode === "loop" ? "Continuous Loop" : "Scheduled"} mode`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = () => {
    setDraftMeta({
      name: channel.name || "", description: channel.description || "",
      category: channel.category || "", language: channel.language || "",
      tags: (channel.tags || []).join(", "),
      resolution: channel.resolution || "1080p",
    });
    setShowEdit(true);
  };
  const metaDirty = () => !!draftMeta && (
    draftMeta.name !== (channel.name || "") ||
    draftMeta.description !== (channel.description || "") ||
    draftMeta.category !== (channel.category || "") ||
    draftMeta.language !== (channel.language || "") ||
    draftMeta.tags !== ((channel.tags || []).join(", ")) ||
    draftMeta.resolution !== (channel.resolution || "1080p"));
  const closeEdit = () => {
    if (metaDirty() && !window.confirm("Discard unsaved changes?")) return;
    setShowEdit(false); setDraftMeta(null);
  };
  const saveMeta = async () => {
    if (!draftMeta.name.trim()) { toast.error("Channel name is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/channels/${id}`, {
        name: draftMeta.name.trim(),
        description: draftMeta.description,
        category: draftMeta.category,
        language: draftMeta.language,
        tags: draftMeta.tags.split(",").map((t) => t.trim()).filter(Boolean),
        resolution: draftMeta.resolution,
      });
      toast.success("Channel updated");
      setShowEdit(false); setDraftMeta(null);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!channel) return <><PageHeader title="Channel" /><Loading /></>;

  const mode = channel.mode || "scheduled";
  const isLoop = mode === "loop";
  const loopRuntime = (channel.loop_playlist || []).reduce((a, e) => a + (e.duration_seconds || 0), 0);
  const totalRuntime = (schedule?.entries || []).reduce((a, e) => a + (e.block?.total_duration || 0), 0);
  const validation = schedule?.validation;

  return (
    <>
      <PageHeader title={channel.name} subtitle={`${channel.resolution} · ${channel.aspect_ratio || "16:9"} · ${isLoop ? "Continuous Loop" : "Scheduled"}`}
        actions={
          <div className="flex items-center gap-2">
            {canEdit && channel.status !== "live" && (
              <button data-testid="channel-golive-btn" onClick={goLive} disabled={busy}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60">
                <Play size={16} weight="fill" /> {busy ? "Publishing…" : "Go Live"}
              </button>
            )}
            {canEdit && channel.status === "live" && (
              <button data-testid="channel-offair-btn" onClick={() => setStatus("off_air")}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-500 transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm">
                <Stop size={16} weight="fill" /> Take Off Air
              </button>
            )}
          </div>
        } />

      <div className="p-8 space-y-6 ccs-enter">
        <button data-testid="back-btn" onClick={() => navigate("/channels")} className="flex items-center gap-1.5 text-xs text-[#737373] hover:text-white transition-colors duration-150">
          <ArrowLeft size={14} /> Back to channels
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {output && (
              <div className="bg-[#141414] border border-[#262626] rounded-sm p-4" data-testid="preview-player-card">
                <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><Broadcast size={15} className="text-emerald-500" /> Live Preview <span className="font-mono text-[10px] text-[#525252] normal-case">(branding overlay as viewers see it)</span></h3>
                {playbackUrl ? (
                  <ChannelPreviewPlayer channelId={id} manifestUrl={playbackUrl} programInfo={nowPlaying?.event?.program_info} />
                ) : (
                  <div data-testid="preview-not-live" className="aspect-video bg-black rounded-sm border border-[#262626] flex items-center justify-center text-center px-6">
                    <p className="text-xs text-[#737373]">Playback authorizes once the channel is live. Go Live to preview it exactly as viewers will (via a secure playback session).</p>
                  </div>
                )}
              </div>
            )}
            <div className="bg-[#141414] border border-[#262626] rounded-sm p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="font-head font-bold text-2xl tracking-tight">{channel.name}</h2>
                    <StatusBadge status={channel.status} testid="channel-status-badge" />
                  </div>
                  <p className="text-sm text-[#A3A3A3] max-w-lg">{channel.description || "No description"}</p>
                  {(channel.category || channel.language || (channel.tags || []).length > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="channel-meta-chips">
                      {channel.category && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-[#3A86FF] bg-[#3A86FF]/10 border-[#3A86FF]/20">{channel.category}</span>}
                      {channel.language && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border text-[#A3A3A3] bg-neutral-500/10 border-neutral-500/20">{channel.language}</span>}
                      {(channel.tags || []).map((t) => <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border text-[#737373] border-[#262626]">#{t}</span>)}
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-[#525252] mt-3 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Created {fmtDate(channel.created_at)}</span>
                    {channel.updated_at && <span data-testid="channel-last-modified">Last Modified {fmtDate(channel.updated_at)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && (
                    <button data-testid="edit-channel-btn" onClick={openEdit}
                      className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline px-2 py-2">
                      <PencilSimple size={14} /> Edit
                    </button>
                  )}
                  {canEdit && channel.status !== "live" && channel.status !== "preparing" && (
                    <button data-testid="channel-trash-btn" onClick={moveToTrash}
                      className="flex items-center gap-1.5 text-xs text-[#737373] hover:text-red-500 px-2 py-2 transition-colors duration-150">
                      <Trash size={16} /> Move to Trash
                    </button>
                  )}
                  {canEdit && (channel.status === "live" || channel.status === "preparing") && (
                    <span data-testid="channel-trash-hint" className="text-[11px] text-[#737373] px-2 py-2">Take off air to enable Trash</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-[#262626]">
                <Metric label={isLoop ? "Content Items" : "Scheduled Blocks"} value={isLoop ? (channel.loop_playlist || []).length : (schedule?.entries?.length || 0)} />
                <Metric label="Total Runtime" value={fmtDuration(isLoop ? loopRuntime : totalRuntime)} />
                <Metric label="Mode" value={isLoop ? "Loop" : "Scheduled"} />
              </div>
            </div>

            {isLoop ? (
              <div className="bg-[#141414] border border-[#262626] rounded-sm" data-testid="loop-content-card">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
                  <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Repeat size={15} className="text-emerald-500" /> Loop Content</h3>
                  <div className="flex items-center gap-3">
                    {canEdit && (
                      <button data-testid="edit-content-btn" onClick={openContent} className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline">
                        <PencilSimple size={14} /> Edit Content
                      </button>
                    )}
                    {canEdit && (
                      <button data-testid="convert-to-scheduled-btn" onClick={() => convertMode("scheduled")} disabled={busy}
                        className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white transition-colors duration-150">
                        <ArrowsClockwise size={14} /> Convert to Scheduled
                      </button>
                    )}
                  </div>
                </div>
                {(channel.loop_playlist || []).length === 0 ? (
                  <div className="p-8 text-center text-sm text-[#737373]">No content yet. Add media to start the loop.</div>
                ) : (
                  <div>
                    {channel.loop_playlist.map((e, i) => (
                      <div key={`${e.asset_id || e.title}-${i}`} data-testid={`loop-item-${i}`} className="flex items-center gap-4 px-5 py-3 border-b border-[#1c1c1c] last:border-0">
                        <span className="font-mono text-sm text-[#525252] w-6">{i + 1}</span>
                        {e.thumbnail && <img src={e.thumbnail} alt="" className="h-8 w-12 object-cover rounded-sm" />}
                        <div className="flex-1"><div className="text-sm">{e.title}</div>
                          <div className="font-mono text-[10px] text-[#525252]">{fmtDuration(e.duration_seconds)}</div></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
            <div className="bg-[#141414] border border-[#262626] rounded-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
                <div className="flex items-center gap-3">
                  <h3 className="font-head font-semibold text-sm">Schedule</h3>
                  {validation && (validation.conflicts.length > 0 || validation.gaps.length > 0 || validation.warnings.length > 0) && (
                    <div className="flex items-center gap-1.5" data-testid="detail-validation">
                      {validation.conflicts.length > 0 && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border text-red-500 bg-red-500/10 border-red-500/20">{validation.conflicts.length} conflict</span>}
                      {validation.gaps.length > 0 && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border text-amber-500 bg-amber-500/10 border-amber-500/20">{validation.gaps.length} gap</span>}
                      {validation.warnings.length > 0 && <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border text-amber-400 bg-amber-500/10 border-amber-500/20">{validation.warnings.length} warn</span>}
                    </div>
                  )}
                  {validation && validation.is_valid && validation.conflicts.length === 0 && validation.gaps.length === 0 && validation.warnings.length === 0 && (schedule?.entries || []).length > 0 && (
                    <span data-testid="detail-validation" className="text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border text-emerald-500 bg-emerald-500/10 border-emerald-500/20">Valid</span>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-3">
                    <button data-testid="open-scheduler-btn" onClick={() => navigate(`/scheduler?channel=${id}`)}
                      className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline">
                      <CalendarBlank size={14} /> Edit in Scheduler
                    </button>
                    <button data-testid="convert-to-loop-btn" onClick={() => convertMode("loop")} disabled={busy}
                      className="flex items-center gap-1.5 text-xs text-[#A3A3A3] hover:text-white transition-colors duration-150">
                      <ArrowsClockwise size={14} /> Convert to Loop
                    </button>
                  </div>
                )}
              </div>
              {(schedule?.entries || []).length === 0 ? (
                <div className="p-8 text-center text-sm text-[#737373]">No blocks scheduled. Open the scheduler to program this channel.</div>
              ) : (
                <div>
                  {schedule.entries.map((e, i) => (
                    <div key={`${e.block_id}-${e.day}-${e.start_time}-${i}`} className="flex items-center gap-4 px-5 py-3 border-b border-[#1c1c1c] last:border-0">
                      <span className="font-mono text-sm text-[#3A86FF] w-14">{e.start_time}</span>
                      <span className="font-mono text-[10px] uppercase text-[#525252] w-10">{e.day}</span>
                      <div className="flex-1">
                        <div className="text-sm">{e.block?.name || "Unknown block"}</div>
                        <div className="font-mono text-[10px] text-[#525252]">{fmtDuration(e.block?.total_duration || 0)} · {e.block?.events?.length || 0} events</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </div>

          <div className="space-y-6">
            <div data-testid="detail-viewers" className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    {viewers > 0 && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />}
                    <span className={`relative inline-flex rounded-full h-2 w-2 ${viewers > 0 ? "bg-emerald-500" : "bg-neutral-600"}`} />
                  </span>
                  Viewers Watching Now
                </div>
                <div data-testid="detail-viewers-count" className={`font-head font-bold text-2xl ${viewers > 0 ? "text-emerald-400" : "text-[#525252]"}`}>{viewers}</div>
              </div>
            </div>
            <div data-testid="detail-now-playing" className={`bg-[#141414] border rounded-sm p-5 ${nowPlaying?.on_air ? "border-emerald-500/30" : "border-[#262626]"}`}>
              <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  {nowPlaying?.on_air && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${nowPlaying?.on_air ? "bg-emerald-500" : "bg-neutral-600"}`} />
                </span>
                Now Playing
              </h3>
              {nowPlaying?.on_air ? (
                <>
                  <div className="text-sm font-medium" data-testid="detail-np-title">{nowPlaying.event.title}</div>
                  <div className="font-mono text-[10px] text-[#525252] mt-1 uppercase tracking-wider">
                    {nowPlaying.event.event_type} · {nowPlaying.block?.name} · event {nowPlaying.event_index + 1}/{nowPlaying.events_total}
                  </div>
                  <div className="h-1 bg-[#0A0A0A] rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-1000"
                      style={{ width: `${nowPlaying.event.duration_seconds ? ((nowPlaying.event.duration_seconds - nowPlaying.remaining_in_event) / nowPlaying.event.duration_seconds) * 100 : 0}%` }} />
                  </div>
                  <div className="flex justify-between font-mono text-[10px] text-[#737373] mt-1.5">
                    <span>{fmtDuration(nowPlaying.elapsed_in_event)} elapsed</span>
                    <span>-{fmtDuration(nowPlaying.remaining_in_event)}</span>
                  </div>
                  {nowPlaying.next_event && <div className="font-mono text-[10px] text-[#525252] mt-2">UP NEXT · {nowPlaying.next_event.title}</div>}
                </>
              ) : (
                <div className="text-xs text-[#737373]">
                  {nowPlaying?.reason === "offline" ? "Channel is not live." : nowPlaying?.reason === "no_schedule" ? "No schedule configured." : "In a schedule gap — no active event."}
                </div>
              )}
            </div>

            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
              <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2"><Broadcast size={16} className="text-emerald-500" /> Distribution (HLS)</h3>
              {!output ? (
                <>
                  <p className="text-xs text-[#737373] mb-4">Generate a stitched HLS manifest that references ReVine asset URLs.</p>
                  {canEdit && (
                    <button data-testid="create-output-btn" onClick={createOutput}
                      className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium py-2 rounded-sm">
                      Generate HLS Output
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Distribution URL (HLS)</div>
                  <div className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded-sm p-2">
                    <code data-testid="distribution-url" className="font-mono text-[10px] text-emerald-400 truncate flex-1">{getDistributionUrl(id)}</code>
                    <button data-testid="copy-distribution-btn" onClick={() => { navigator.clipboard.writeText(getDistributionUrl(id)); toast.success("Distribution URL copied"); }}
                      className="text-[#737373] hover:text-white transition-colors duration-150"><Copy size={14} /></button>
                  </div>
                  <p className="text-[10px] text-[#525252] mt-2">Permanent HLS endpoint — paste into ReVine TV or any compatible player.</p>
                  {playbackUrl ? (
                    <button data-testid="open-live-preview-btn" onClick={() => window.open(playbackUrl, "_blank", "noreferrer")}
                      className="block w-full text-center text-xs text-[#3A86FF] hover:underline mt-3">Open Live Preview →</button>
                  ) : (
                    <p data-testid="preview-not-live-hint" className="text-[10px] text-[#525252] mt-3 text-center">Live Preview available once the channel is live.</p>
                  )}
                </>
              )}
            </div>

            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5" data-testid="readiness-card">
              <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2">
                {readiness?.ready ? <CheckCircle size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-amber-500" />}
                Publication Readiness
              </h3>
              <div className="space-y-2">
                {(readiness?.checks || []).map((c) => (
                  <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`readiness-${c.key}`}>
                    {c.ok ? <CheckCircle size={15} className="text-emerald-500" /> : <XCircle size={15} className="text-red-500" />}
                    <span className={c.ok ? "text-[#A3A3A3]" : "text-white"}>{c.label}</span>
                  </div>
                ))}
              </div>
              <div className={`mt-4 text-center text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-sm border ${
                readiness?.ready ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" : "text-amber-500 bg-amber-500/10 border-amber-500/20"}`}>
                {readiness?.ready ? "Ready for Publication" : "Not Ready"}
              </div>
            </div>

            <div className="bg-[#141414] border border-[#262626] rounded-sm p-5" data-testid="brand-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-head font-semibold text-sm flex items-center gap-2"><ImageSquare size={16} className="text-[#3A86FF]" /> Branding</h3>
                {canEdit && (
                  <button data-testid="edit-brand-btn" onClick={openBrand} className="flex items-center gap-1.5 text-xs text-[#3A86FF] hover:underline">
                    <PencilSimple size={14} /> Edit
                  </button>
                )}
              </div>
              <BrandPreview brand={channel.brand || {}} />
              <div className="font-mono text-[10px] text-[#525252] mt-3 uppercase tracking-wider">
                Logo {channel.brand?.logo ? "set" : "none"} · {(channel.brand?.logo_position || "top_right").replace("_", " ")} · {channel.brand?.opacity ?? 100}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {showContent && (
        <Modal title="Edit Loop Content" size="2xl" onClose={() => setShowContent(false)}>
          <div className="w-full">
            <ContentPicker value={draftPlaylist} onChange={setDraftPlaylist} listHeight="max-h-[38vh] md:max-h-[58vh]" />
            <div className="sticky bottom-0 mt-3 pt-2 bg-[#141414]">
            <button data-testid="save-content-btn" onClick={savePlaylist} disabled={busy}
              className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm disabled:opacity-60">
              {busy ? "Saving…" : "Save Content"}
            </button>
            </div>
          </div>
        </Modal>
      )}

      {showBrand && draftBrand && (
        <Modal title="Edit Branding" size="lg" onClose={() => setShowBrand(false)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <BrandControls brand={draftBrand} onChange={setDraftBrand} />
            <div className="space-y-3">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] font-bold">Live Preview</label>
              <BrandPreview brand={draftBrand} />
              <button data-testid="save-brand-btn" onClick={saveBrand} disabled={busy}
                className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm disabled:opacity-60">
                {busy ? "Saving…" : "Save Branding"}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {showEdit && draftMeta && (
        <Modal title="Edit Channel" onClose={closeEdit} wide>
          <div className="space-y-4">
            <ModalField label="Channel Name" testid="edit-channel-name"
              value={draftMeta.name} onChange={(e) => setDraftMeta({ ...draftMeta, name: e.target.value })} placeholder="Channel name" />
            <ModalTextarea label="Description" testid="edit-channel-description"
              value={draftMeta.description} onChange={(e) => setDraftMeta({ ...draftMeta, description: e.target.value })} placeholder="Short description" />
            <div className="grid grid-cols-2 gap-4">
              <ModalField label="Category" testid="edit-channel-category"
                value={draftMeta.category} onChange={(e) => setDraftMeta({ ...draftMeta, category: e.target.value })} placeholder="e.g. Faith, Movies" />
              <ModalField label="Language" testid="edit-channel-language"
                value={draftMeta.language} onChange={(e) => setDraftMeta({ ...draftMeta, language: e.target.value })} placeholder="e.g. English" />
            </div>
            <ModalField label="Tags (comma separated)" testid="edit-channel-tags"
              value={draftMeta.tags} onChange={(e) => setDraftMeta({ ...draftMeta, tags: e.target.value })} placeholder="e.g. classic, family, 24/7" />
            <div>
              <ModalSelect label="Resolution" testid="edit-channel-resolution"
                value={draftMeta.resolution} onChange={(e) => setDraftMeta({ ...draftMeta, resolution: e.target.value })}>
                <option value="1080p">1080p — Full HD</option>
                <option value="720p">720p — HD</option>
                <option value="480p">480p — SD</option>
              </ModalSelect>
              <p className="text-[11px] text-[#737373] mt-1.5">Channel setting used for labelling and output metadata. It does not re-encode or convert your source media.</p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button data-testid="save-channel-btn" onClick={saveMeta} disabled={busy}
                className="flex-1 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm disabled:opacity-60">
                {busy ? "Saving…" : "Save Changes"}
              </button>
              <button data-testid="cancel-channel-edit-btn" onClick={closeEdit}
                className="px-5 py-2.5 rounded-sm border border-[#262626] text-[#A3A3A3] hover:text-white hover:border-[#3A3A3A] transition-colors duration-150 text-sm">
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="font-head font-bold text-xl tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mt-0.5">{label}</div>
    </div>
  );
}
